import { cn } from '@/shared/utils'

/**
 * AED badge — outlined rectangle with "AED" text inside,
 * exactly like Option 1 from the preview.
 */
export function Dirham({
  size = 14,
  className,
}: {
  size?: number
  className?: string
}) {
  // Badge proportions: width ≈ 2.4× height
  const h = size
  const w = Math.round(size * 2.4)
  const r = Math.round(size * 0.25)          // corner radius
  const sw = Math.max(1, size * 0.09)        // stroke width
  const fs = Math.round(size * 0.65)         // font size inside badge

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('inline-block align-middle flex-shrink-0', className)}
      aria-label="AED"
    >
      {/* Outlined rectangle */}
      <rect
        x={sw / 2}
        y={sw / 2}
        width={w - sw}
        height={h - sw}
        rx={r}
        stroke="currentColor"
        strokeWidth={sw}
        fill="none"
      />
      {/* "AED" text centered inside */}
      <text
        x={w / 2}
        y={h / 2 + fs * 0.36}
        textAnchor="middle"
        fill="currentColor"
        fontSize={fs}
        fontWeight="700"
        fontFamily="inherit"
        letterSpacing="0.4"
      >
        AED
      </text>
    </svg>
  )
}

/**
 * Inline currency amount with AED badge:
 *   [AED] 1,000
 *
 * Usage:
 *   <DirhamAmount value={1000}/>
 *   <DirhamAmount value={pref.min_price} size={16} className="text-gray-900"/>
 */
export function DirhamAmount({
  value,
  size = 13,
  className,
  textClass,
}: {
  value: number | string | null | undefined
  size?: number
  className?: string
  textClass?: string
}) {
  if (value == null || value === '') {
    return <span className="text-gray-400">—</span>
  }

  const num = typeof value === 'number' ? value : parseFloat(String(value))
  const formatted = isNaN(num)
    ? String(value)
    : num.toLocaleString('en-AE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <Dirham size={size} className={cn('flex-shrink-0', textClass)}/>
      <span className={cn('font-semibold tabular-nums', textClass)}>
        {formatted}
      </span>
    </span>
  )
}
